import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const STORAGE_KEY = 'nimo_lang';

/**
 * Detect whether the visitor is likely from mainland China.
 * Strategy (no backend needed):
 * 1. Timezone name contains "Shanghai" or "Chongqing" → definitely China
 * 2. Timezone offset is UTC+8 AND browser language is Chinese → likely China
 * 3. Browser language is zh-CN / zh-Hans → likely China (overseas Chinese using zh-TW still get zh, which is fine)
 * Non-Chinese regions (Singapore, Malaysia, Philippines are UTC+8 but use en/ms/fil) → correctly get English
 */
function detectChineseRegion(): boolean {
  // 1. Timezone name check (most reliable)
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz === 'Asia/Shanghai' || tz === 'Asia/Chongqing' || tz === 'Asia/Harbin' || tz === 'Asia/Urumqi') {
      return true;
    }
  } catch {
    // ignore
  }

  // 2. Browser language check
  const langs = navigator.languages || [navigator.language];
  const isChinese = langs.some((l) => {
    const lower = l.toLowerCase();
    // zh-cn, zh-hans, zh (generic) → Chinese mainland
    // zh-tw, zh-hk, zh-hant → Traditional Chinese, still show zh
    return lower.startsWith('zh');
  });

  // 3. UTC+8 + Chinese language = likely China/HK/TW (show zh)
  const tzOffset = new Date().getTimezoneOffset();
  const isUTC8 = tzOffset === -480;

  // Chinese language → show zh (covers mainland, HK, TW, overseas Chinese)
  // UTC+8 alone (Singapore, Malaysia, Philippines) → show en
  return isChinese;
}

export function getInitialLanguage(): string {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved === 'zh' || saved === 'en') return saved;
  return detectChineseRegion() ? 'zh' : 'en';
}

export function useLanguage() {
  const { i18n } = useTranslation();

  const currentLang = i18n.language?.startsWith('zh') ? 'zh' : 'en';

  const switchLang = useCallback(
    (lang: 'zh' | 'en') => {
      localStorage.setItem(STORAGE_KEY, lang);
      i18n.changeLanguage(lang);
    },
    [i18n]
  );

  const toggleLang = useCallback(() => {
    switchLang(currentLang === 'zh' ? 'en' : 'zh');
  }, [currentLang, switchLang]);

  return { currentLang, switchLang, toggleLang };
}
